'use client'

import { useTranslations } from 'use-intl'
import type { AppMessages } from '@/i18n/config'
import { resolveCodeLabel } from '@/i18n/labels'
import type { SapTarget } from '@/lib/ap/workbench'

type StatusKey = keyof AppMessages['status']
type ReviewCategoryKey = keyof AppMessages['reviewCategory']
type ExplorerTabKey = keyof AppMessages['explorerTab']
type SapStepMessageKey = keyof AppMessages['sapStep']

/**
 * Labels for the codes the AP backend sends (invoice statuses, review categories, explorer tabs, SAP posting
 * steps) in the reader's language. A code this build doesn't know shows the server's label, or the code made
 * readable, so a new backend value never breaks the page.
 */
export function useApLabels() {
  const tStatus = useTranslations('status')
  const tCategory = useTranslations('reviewCategory')
  const tTab = useTranslations('explorerTab')
  const tSap = useTranslations('sapStep')

  const knownSapStep = (key: string): string | null =>
    tSap.has(key as SapStepMessageKey) ? tSap(key as SapStepMessageKey) : null

  return {
    status: (code: string): string =>
      resolveCodeLabel(code, (c) => (tStatus.has(c as StatusKey) ? tStatus(c as StatusKey) : null)),
    reviewCategory: (code: string | null | undefined): string =>
      code
        ? resolveCodeLabel(code, (c) =>
            tCategory.has(c as ReviewCategoryKey) ? tCategory(c as ReviewCategoryKey) : null,
          )
        : '—',
    explorerTab: (value: string, serverLabel?: string | null): string =>
      resolveCodeLabel(value, (c) => (tTab.has(c as ExplorerTabKey) ? tTab(c as ExplorerTabKey) : null), serverLabel),
    knownSapStep,
    sapStep: (key: string, target?: SapTarget | null, serverLabel?: string | null): string =>
      key === 'check_connection' && target
        ? tSap('check_connection_target', { system: target.system_id, client: target.client })
        : resolveCodeLabel(key, knownSapStep, serverLabel),
  }
}
